/* Mock routes — AI services (recommendations and disease detection). */

import type {
  DiseaseReport,
  Governorate,
  PlantPurpose,
  Recommendation,
  SoilType,
  Species,
  SunExposure,
  WaterAvailability,
} from '@core/types'
import { ApiError } from '../../http'
import { db, nextId } from '../store'
import { CLIMATE_BY_GOVERNORATE } from '../data/climate'
import { matchesAny, readNumber, readString, type HandlerContext, type MockRoute } from '../types'
import { requirePlant } from './shared'
import { scoreSpecies } from './ai-scoring'

const GENERIC_DIAGNOSIS = {
  label: 'Nutrient deficiency (nitrogen)',
  labelAr: 'نقص النيتروجين',
  confidence: 0.63,
  severity: 'low' as const,
  description:
    'Uniform pale green colour across the older leaves with no spots or lesions. This is usually a feeding or leaching problem rather than an infection.',
  treatmentSteps: [
    'Apply a balanced fertiliser and water it in thoroughly.',
    'Check that irrigation actually reaches the root zone and is not running off.',
    'Photograph the same leaves again in 14 days so the change is comparable.',
  ],
  nurseryAdvice:
    'Show the photo to a verified nursery if the colour has not improved within two weeks.',
}

function recommend(context: HandlerContext): Recommendation[] {
  const governorate = (readString(context.body, 'governorate') ?? 'Baghdad') as Governorate
  const soilType = (readString(context.body, 'soilType') ?? 'loamy') as SoilType
  const sunExposure = (readString(context.body, 'sunExposure') ?? 'full-sun') as SunExposure
  const waterAvailability = (readString(context.body, 'waterAvailability') ??
    'moderate') as WaterAvailability
  const purpose = (readString(context.body, 'purpose') ?? 'shade') as PlantPurpose
  const limit = readNumber(context.body, 'limit') ?? 6

  if (!CLIMATE_BY_GOVERNORATE[governorate]) {
    throw new ApiError({
      status: 422,
      code: 'unknown_governorate',
      message: `We do not have climate data for "${governorate}" yet, so we cannot score species for it.`,
    })
  }

  return db.species
    .map((species: Species) => {
      const scored = scoreSpecies(species, {
        governorate,
        soilType,
        sunExposure,
        waterAvailability,
        purpose,
      })
      return {
        ...species,
        matchScore: scored.score,
        matchReasons: scored.reasons,
        watchOuts: scored.watchOuts,
      }
    })
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, Math.max(1, Math.min(limit, 12)))
}

export const aiRoutes: MockRoute[] = [
  {
    method: 'POST',
    pattern: '/ai/recommendations',
    handler: (context) => recommend(context),
    emptyState: [] satisfies Recommendation[],
  },
  {
    method: 'POST',
    pattern: '/ai/disease-detection',
    handler: ({ body }) => {
      const plantId = readString(body, 'plantId')
      const imageLabel = readString(body, 'imageLabel') ?? 'leaf-photo.jpg'
      const notes = readString(body, 'symptomNotes') ?? ''
      const plant = plantId ? requirePlant(plantId) : undefined
      const plantName = plant?.nickname ?? 'Unassigned scan'

      // The failure path is reachable on purpose instead of being hidden.
      if (matchesAny(`${imageLabel} ${notes}`, ['blur', 'dark', 'out of focus'])) {
        const failed: DiseaseReport = {
          id: nextId('dr', 300),
          plantId: plantId ?? null,
          plantName,
          capturedAt: new Date().toISOString(),
          status: 'failed',
          failureReason:
            'The photo is too dark or out of focus for the model to separate the leaf from the background. Retake it in daylight with one leaf filling most of the frame.',
          modelVersion: 'mobilenetv3-iq-0.4',
          imageLabel,
        }
        db.diseaseReports.unshift(failed)
        return failed
      }

      const previous = plant
        ? db.diseaseReports.find((report) => report.diagnosis && report.plantId === plant.id)
        : undefined

      const report: DiseaseReport = {
        id: nextId('dr', 300),
        plantId: plantId ?? null,
        plantName,
        capturedAt: new Date().toISOString(),
        status: 'complete',
        modelVersion: 'mobilenetv3-iq-0.4',
        imageLabel,
        diagnosis: previous?.diagnosis ?? GENERIC_DIAGNOSIS,
      }
      db.diseaseReports.unshift(report)

      if (plant) {
        db.activity.unshift({
          id: nextId('ac', 700),
          kind: 'disease-scan',
          title: 'Disease scan completed',
          detail: `${report.diagnosis?.label} on ${plantName} · ${Math.round(
            (report.diagnosis?.confidence ?? 0) * 100,
          )}% confidence`,
          occurredAt: report.capturedAt,
          points: 0,
        })
      }

      return report
    },
  },
  {
    method: 'GET',
    pattern: '/ai/disease-reports',
    handler: ({ query }) => {
      const plantId = query.get('plantId')
      const reports = plantId
        ? db.diseaseReports.filter((report) => report.plantId === plantId)
        : db.diseaseReports
      return [...reports].sort(
        (a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime(),
      )
    },
    emptyState: [] satisfies DiseaseReport[],
  },
  {
    method: 'GET',
    pattern: '/ai/disease-reports/:id',
    handler: ({ params }) => {
      const report = db.diseaseReports.find((item) => item.id === params.id)
      if (!report) {
        throw new ApiError({
          status: 404,
          code: 'report_not_found',
          message: 'That scan result is no longer available. Run a new scan to see a diagnosis.',
        })
      }
      return report
    },
  },
  {
    method: 'GET',
    pattern: '/ai/species',
    handler: () => db.species,
    emptyState: [] satisfies Species[],
  },
]
